import React, { useRef, useEffect, useState } from "react";
import { Routes, Route, useLocation } from 'react-router-dom';
import Coverage from './components/mini-coverage';
import Archangel from './components/Archangel';
import { Link } from 'react-router-dom';
import Careers from './components/Careers';
import Privacy from './components/Privacy';
import About from './components/About';
import Veritas from './components/Veritas';
import Movement from './components/Phantom';
import Phantom from "./components/Phantom";
import CoverageSolo from "./components/Coverage";
import A16Z from "./components/A16Z";

const ANIMATION_DELAY_BASE = 150; // milliseconds between each item

export default function App() {
  const heroVideoRef = useRef(null);
  const containerRef = useRef(null);
  const problemSectionRef = useRef(null);
  const portalSectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPortalVisible, setIsPortalVisible] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isInPortalSection, setIsInPortalSection] = useState(false);
  const [isFullyInPortalSection, setIsFullyInPortalSection] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [hasStartedAnimation, setHasStartedAnimation] = useState(false);
  const [hasAutoScrolled, setHasAutoScrolled] = useState(false);
  const [activeFormat, setActiveFormat] = useState('livestream');
  const location = useLocation();

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.3 } // Trigger when 30% of the section is visible
    );

    if (problemSectionRef.current) {
      observer.observe(problemSectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const portalObserver = new IntersectionObserver(
      ([entry]) => {
        console.log('Portal Observer:', {
          isIntersecting: entry.isIntersecting,
          intersectionRatio: entry.intersectionRatio
        });

        setIsFullyInPortalSection(entry.intersectionRatio > 0.9);
        setIsPortalVisible(entry.isIntersecting);

        if (entry.isIntersecting) {
          setIsInPortalSection(true);
          console.log('Entered portal section');
        } else {
          setIsInPortalSection(false);
          setScrollProgress(0);
          setHasStartedAnimation(false);
          console.log('Left portal section, reset states');
        }
      },
      { threshold: [0.9] }
    );

    if (portalSectionRef.current) {
      portalObserver.observe(portalSectionRef.current);
    }

    return () => portalObserver.disconnect();
  }, []);

  useEffect(() => {
    if (isInPortalSection && !hasStartedAnimation) {
      setHasStartedAnimation(true);

      // Introduce a delay before starting the animation
      const delay = 1000; // 1 second delay

      setTimeout(() => {
        let start = null;

        const animate = (timestamp) => {
          if (!start) start = timestamp;
          const elapsed = timestamp - start;

          const duration = 2000; // 2 seconds for portal animation
          const progress = Math.min(1, elapsed / duration);
          setScrollProgress(progress);

          if (progress < 1) {
            requestAnimationFrame(animate);
          } else {
            // Jump to the next section after animation is done
            const demoSection = document.querySelector('#black-demo-section');
            if (demoSection) {
              demoSection.scrollIntoView({ behavior: 'smooth' });
            }
          }
        };

        requestAnimationFrame(animate);
      }, delay);
    }
  }, [isInPortalSection, hasStartedAnimation]);

  useEffect(() => {
    // Wait for the video and images to load
    const video = heroVideoRef.current;
    const images = document.querySelectorAll('img');

    Promise.all([
      // Wait for video to be loaded
      new Promise((resolve) => {
        if (video) {
          video.onloadeddata = () => resolve();
          if (video.readyState >= 3) resolve();
        } else resolve();
      }),
      // Wait for all images to load
      Promise.all(
        Array.from(images).map(
          img => new Promise((resolve) => {
            if (img.complete) resolve();
            else img.onload = () => resolve();
          })
        )
      )
    ]).then(() => {
      // Add a small delay to ensure smooth transition
      setTimeout(() => setIsLoaded(true), 100);
    });
  }, []);

  const scrollToNextSection = () => {
    const sections = containerRef.current.querySelectorAll('.snap-start');
    const currentScroll = window.scrollY;
    const viewportHeight = window.innerHeight;

    // Find the next section
    for (let section of sections) {
      const sectionTop = section.offsetTop;
      if (sectionTop > currentScroll) {
        section.scrollIntoView({ behavior: 'smooth' });
        break;
      }
    }
  };

  const shouldAnimatePortal = isLoaded && isFullyInPortalSection;

  const styles = {
    solutionSection: {
      marginBottom: '40px', // Adjust as needed
    },
  };

  // If we're on a specific page route, only render that component
  if (location.pathname === '/archangel') {
    return <Archangel />;
  } else if (location.pathname === '/careers') {
    return <Careers />;
  } else if (location.pathname === '/privacy') {
    return <Privacy />;
  } else if (location.pathname === '/about') {
    return <About />;
  } else if (location.pathname === '/veritas') {
    return <Veritas />;
  } else if (location.pathname === '/phantom') {
    return <Phantom />;
  } else if (location.pathname === '/coverage') {
    return <CoverageSolo />;
  } else if (location.pathname === '/a16z') {
    return <A16Z />;
  }

  // Otherwise render the main homepage content
  return (
    <div ref={containerRef} className="h-screen w-full bg-black md:snap-y md:snap-mandatory overflow-y-auto overflow-x-hidden">
      {/* Hero Section */}
      <div className="h-screen relative md:snap-start flex items-center justify-center pt-16">
        <video
          className="absolute top-0 left-0 w-full h-full object-cover"
          src="/videos/movement.mp4"
          autoPlay
          playsInline
          loop
          muted
          ref={heroVideoRef}
        />
        {/* Black Tint Overlay */}
        <div className="absolute top-0 left-0 w-full h-full bg-black opacity-70"></div>

        {/* Gradient overlay just for metrics and logos section */}
        <div className="absolute bottom-0 left-0 w-full h-[300px] bg-gradient-to-t from-black/40 via-black/30 to-transparent backdrop-blur-sm"></div>

        <div className="container mx-auto px-4 text-center relative z-10 h-full flex flex-col">
          {/* Main content wrapper */}
          <div className="flex-1 flex flex-col items-center justify-center">
            {/* Headline */}
            <h1 className="text-4xl md:text-6xl text-white font-light mb-4">
              Datasets for the Physical World            </h1>
            {/* Sub-headline */}
            <p className="text-xl md:text-4xl text-white/90 font-light mb-8">
              Trillion-dollar decisions rely on bad data.
              Our unified camera network in San Francisco fixes that.            </p>
            {/* CTA */}
            <div
              className="mt-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full text-white font-medium tracking-wide transform transition hover:scale-105 hover:shadow-lg focus:outline-none cursor-pointer max-w-xs mx-auto"
              onClick={() => document.getElementById('coverage-section').scrollIntoView({ behavior: 'smooth' })}
            >
              <p className="text-lg md:text-xl font-light leading-none">
                See the cameras →
              </p>
            </div>
          </div>

          {/* Metrics and logos section */}
          <div className="relative">
            {/* Social Proof Metrics Ribbon */}
            <div className="mb-8 md:mb-12 flex flex-col md:flex-row items-center justify-center gap-6 md:gap-16">
              <div className="flex items-center gap-2 md:gap-3">
                <span className="text-3xl md:text-5xl font-light text-white">50</span>
                <span className="text-xs md:text-base text-white/70">live cameras</span>
              </div>
              <div className="h-8 w-px bg-white/20 hidden md:block" />
              <a
                href="#case-studies"
                onClick={() => document.getElementById('archangel-case').play()}
                className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity"
              >
                <span className="text-3xl md:text-5xl font-light text-white">1</span>
                <span className="text-xs md:text-base text-white/70">major crime solved</span>
              </a>
              <div className="h-8 w-px bg-white/20 hidden md:block" />
              <a
                href="#case-studies"
                onClick={() => document.getElementById('parking-karma-case').play()}
                className="flex items-center gap-2 md:gap-3 hover:opacity-80 transition-opacity"
              >
                <span className="text-3xl md:text-5xl font-light text-white">50</span>
                <span className="text-xs md:text-base text-white/70">hr driver time saved</span>
              </a>
            </div>

            {/* Trusted By Section */}
            <div className="mb-16">
              <p className="text-white/50 text-sm mb-8">Trusted by</p>
              <div className="grid grid-cols-2 md:flex md:flex-wrap justify-center items-center gap-8 md:gap-20">
                <img
                  src="/images/sfpd.png"
                  alt="SFPD"
                  className="h-16 md:h-20 mx-auto"
                />
                <img
                  src="/images/jpm.png"
                  alt="JP Morgan"
                  className="h-16 md:h-20 mx-auto"
                />
                <img
                  src="/images/techstars.png"
                  alt="Techstars"
                  className="h-16 md:h-20 mx-auto"
                />
                <img
                  src="/images/boost.png"
                  alt="Boost VC"
                  className="h-16 md:h-20 mx-auto"
                />
              </div>
            </div>
          </div>
        </div>
      </div>


      {/* Coverage Section */}
      <div id="coverage-section" className="h-[100vh] sm:h-[200vh] md:h-screen relative md:snap-start bg-black flex items-center justify-center">
        <div className="absolute top-0 w-full text-center px-6 md:px-4 py-4 md:py-8 lg:py-12 z-20">
          <p className="text-lg md:text-xl font-light text-blue-100" style={{ marginTop: '100px', marginBottom: '100px' }}>
            Our growing network of live cameras in San Francisco.
          </p>
        </div>

        <div className="w-full max-w-full md:max-w-4xl h-[50vh] md:h-3/4 border border-white/20 rounded-lg shadow-lg mt-8 mb-8 md:mt-24 md:mb-24">
          <div className="h-full bg-gradient-to-b from-black via-gray-900 to-black text-white">
            <Coverage />
          </div>
        </div>
        <img src="/images/beige-spur.svg" alt="" className="absolute top-8 right-8 w-16 h-16 hidden md:block" />

      </div>



      {/* What Our Cameras See Section */}
      <div className="relative md:snap-start bg-black min-h-screen flex flex-col justify-center py-8 md:py-16">
        <div>
          {/* Title */}
          <div className="text-center max-w-4xl mx-auto px-4 mb-8 md:mb-12">
            <h2 className="text-2xl md:text-4xl text-white font-light mb-2">
              What our cameras see
            </h2>
            {/* Horizontal break line */}
            <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto my-3"></div>
            <p className="text-base md:text-xl text-white/80 font-light md:max-w-3xl mx-auto">
              We transpose the physical world into data
            </p>
          </div>

          {/* Video Container */}
          <div className="max-w-4xl mx-auto px-4">
            <div className="relative w-full aspect-video rounded-lg overflow-hidden border border-white/10">
              <iframe
                className="absolute top-0 left-0 w-full h-full"
                src="https://www.youtube.com/embed/xCdguJXnY98?autoplay=0&controls=1&modestbranding=1&rel=0"
                title="What Our Cameras See"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              ></iframe>
            </div>
          </div>
        </div>
        <img src="/images/beige-spur.svg" alt="" className="absolute top-8 right-8 w-16 h-16 hidden md:block" />
      </div>

      {/* Format Section */}
      <div className="relative md:snap-start bg-black min-h-screen flex flex-col justify-between py-8 md:py-16">
        <div>
          {/* Title */}
          <div className="text-center max-w-4xl mx-auto px-4 mb-4 md:mb-6">
            <h2 className="text-2xl md:text-4xl text-white font-light mb-2">
              How do I get it?
            </h2>
            <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto my-3"></div>
          </div>

          {/* Format Grid */}
          <div className="max-w-3xl mx-auto px-4 mb-2 md:mb-4">
            <div className="grid grid-cols-1 gap-4">
              {[
                {
                  id: 'livestream',
                  format: "Live stream (WebRTC)",
                  useCase: "Watch any camera feed in real-time, with <1s latency",
                  className: "from-blue-600/20 to-blue-400/20",
                  content: (
                    <div className="mt-4 w-full">
                      <div className="bg-black/30 rounded-lg p-4 mb-4">
                        <p className="text-white/80 mb-2">Secure video link:</p>
                        <a
                          href="https://ipcamlive.com/6769bac543ef3"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 transition-colors break-all"
                        >
                          https://ipcamlive.com/6769bac543ef3
                        </a>
                      </div>
                      <div className="relative w-full aspect-video">
                        <iframe
                          src="https://g1.ipcamlive.com/player/playback.php?alias=6769bac543ef3&date=-2 days"
                          className="w-full h-full rounded-lg"
                          frameBorder="0"
                          allowFullScreen
                        />
                      </div>
                    </div>
                  )
                },
                {
                  id: 'clip',
                  format: "Clip URL",
                  useCase: "Share timestamped, tamper-proof video evidence",
                  className: "from-purple-600/20 to-purple-400/20",
                  content: (
                    <div className="mt-4 w-full">
                      <div className="bg-black/30 rounded-lg p-4 mb-4">
                        <p className="text-white/80 mb-2">Secure video link:</p>
                        <a
                          href="https://storage.googleapis.com/background-for-orchestra/crash5.mp4"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-400 hover:text-blue-300 transition-colors break-all"
                        >
                          https://storage.googleapis.com/background-for-orchestra/crash5.mp4
                        </a>
                      </div>
                      <div className="relative w-full aspect-video">
                        <video
                          className="w-full h-full rounded-lg"
                          controls
                          preload="metadata"
                        >
                          <source src="https://storage.googleapis.com/background-for-orchestra/crash5.mp4" type="video/mp4" />
                          Your browser does not support the video tag.
                        </video>
                      </div>
                    </div>
                  )
                },
                {
                  id: 'api',
                  format: "Real-time API",
                  useCase: "Build apps on live data: traffic, parking, foot traffic, etc.",
                  className: "from-green-600/20 to-green-400/20",
                  content: (
                    <div className="mt-4 w-full">
                      <div className="bg-black/30 rounded-lg p-4 font-mono text-sm mb-4">
                        <pre className="text-green-400">
                          {`curl "https://api.orchestra.com/v1/data"
-H "Authorization: Bearer $API_KEY"
-d '{"query": "parking_spots"}'`}
                        </pre>
                      </div>
                      <div className="bg-black/30 rounded-lg p-4">
                        <p className="text-white/80 mb-2">Sample response (ParkingKarma data):</p>
                        <div className="max-h-[300px] overflow-y-auto custom-scrollbar">
                          <pre className="text-xs text-green-400 whitespace-pre-wrap">
                            {JSON.stringify([
                              {
                                "id": 1085,
                                "location": "1 Ave @ 23 St",
                                "lat": 40.736494475991016,
                                "lon": -73.97790490445611,
                                "comments": null,
                                "parking_sign_url": "https://storage.googleapis.com/parking-schedule-signs/cam_id_361_inside.png"
                              },
                              {
                                "id": 1095,
                                "location": "Park Ave @ 34 St",
                                "lat": 40.74690199832739,
                                "lon": -73.98168060335189,
                                "comments": null,
                                "parking_sign_url": "https://storage.googleapis.com/parking-schedule-signs/cam_id_419.png"
                              },
                              {
                                "id": 1106,
                                "location": "2 Ave @ 49 St",
                                "lat": 40.75448427475471,
                                "lon": -73.96872955257423,
                                "comments": null,
                                "parking_sign_url": "https://storage.googleapis.com/parking-schedule-signs/cam_id_366.png"
                              }
                            ], null, 2)}
                          </pre>
                        </div>
                      </div>
                      <style jsx>{`
                        .custom-scrollbar::-webkit-scrollbar {
                          width: 8px;
                        }
                        .custom-scrollbar::-webkit-scrollbar-track {
                          background: rgba(0, 0, 0, 0.2);
                          border-radius: 4px;
                        }
                        .custom-scrollbar::-webkit-scrollbar-thumb {
                          background: rgba(74, 222, 128, 0.3);
                          border-radius: 4px;
                        }
                        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                          background: rgba(74, 222, 128, 0.5);
                        }
                      `}</style>
                    </div>
                  )
                },
                {
                  id: 'dashboard',
                  format: "Dashboard",
                  useCase: "Track key metrics and spot trends across your network",
                  className: "from-amber-600/20 to-amber-400/20",
                  content: (
                    <div className="mt-4 w-full">
                      <img
                        src="/images/dashboard.png"
                        alt="Dashboard Preview"
                        className="w-full rounded-lg border border-white/10"
                      />
                    </div>
                  )
                },
                {
                  id: 'custom',
                  format: "Request custom dataset",
                  useCase: "e.g. How many Waymos/Teslas pass by each day",
                  className: "from-pink-600/20 to-pink-400/20",
                  content: (
                    <div className="mt-4 w-full">
                      <div className="bg-black/30 rounded-lg p-4">
                        <p className="text-white/80 mb-4">Tell us what you want to measure, and we'll build a custom dataset for you.</p>
                        <button
                          onClick={() => document.getElementById('contact-section').scrollIntoView({ behavior: 'smooth' })}
                          className="text-blue-400 hover:text-blue-300 transition-colors"
                        >
                          Contact us →
                        </button>
                      </div>
                    </div>
                  )
                }
              ].map((item) => (
                <div
                  key={item.id}
                  className={`bg-gradient-to-br ${item.className} rounded-lg p-6 border border-white/10 backdrop-blur-sm transition-all duration-300 cursor-pointer
                    ${activeFormat === item.id ? 'scale-100' : 'scale-98 hover:scale-99'}`}
                  onClick={() => setActiveFormat(activeFormat === item.id ? null : item.id)}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="text-xl md:text-2xl text-white font-light mb-2">
                        {item.format}
                      </h3>
                      <p className="text-white/80 font-light">
                        {item.useCase}
                      </p>
                    </div>
                    <div className={`transform transition-transform duration-300 ${activeFormat === item.id ? 'rotate-180' : ''}`}>
                      <svg className="w-6 h-6 text-white/60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>

                  {/* Expandable Content */}
                  <div className={`overflow-hidden transition-all duration-300 ${activeFormat === item.id ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                    {item.content}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* CTA Section */}
        <div className="text-center mt-8">
          <p className="text-center text-white/80 font-light md:max-w-3xl mx-auto mb-4">
            Want to see how this data can work for you?
          </p>
          <a
            href="https://calendly.com/drake-orchestraos"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full text-white font-medium tracking-wide transform transition hover:scale-105 hover:shadow-lg focus:outline-none cursor-pointer max-w-xs mx-auto inline-block"
          >
            <p className="text-lg md:text-xl font-light leading-none">
              Request a demo
            </p>
          </a>
        </div>
        <img src="/images/beige-spur.svg" alt="" className="absolute top-8 right-8 w-16 h-16 hidden md:block" />
      </div>


      {/* Solution Section */}
      <div className="relative md:snap-start bg-black min-h-screen flex flex-col justify-between py-8 md:py-16">
        <div>
          {/* Title */}
          <div className="text-center max-w-4xl mx-auto px-4 mb-4 md:mb-6">
            <h2 className="text-2xl md:text-4xl text-white font-light mb-2">
              With our camera network and AI, we translate the physical world into data.
            </h2>
            {/* Horizontal break line */}
            <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto my-3"></div>
            <p className="text-base md:text-xl text-white/80 font-light md:max-w-3xl mx-auto">
              Because we own the infrastructure, we're the only ones delivering real-time, 24/7 insights for any industry.
            </p>
          </div>

          {/* Table */}
          <div className="max-w-6xl mx-auto px-4 mb-2 md:mb-4">
            <div className="overflow-x-auto">
              {/* Mobile cards with scroll */}
              <div className="block md:hidden max-h-[50vh] overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
                {[
                  { feature: 'Type', orchestra: '24/7 video footage', satellite: 'Still images', gps: 'Location pings', dashcams: 'Vehicle-based video', government: 'Mostly still images, some video' },
                  { feature: 'Coverage', orchestra: 'Unified, citywide', satellite: 'Large area, low res', gps: 'Fragmented', dashcams: 'Fragmented, moving vehicles', government: 'Spotty, intersection-focused' },
                  { feature: 'Update Speed', orchestra: 'Real-time, continuous', satellite: 'Slow refresh', gps: 'Laggy, incomplete', dashcams: 'Not citywide, not shared', government: 'Delayed, not always accessible' },
                  { feature: 'Data Quality', orchestra: 'Ground-truth, high-res video', satellite: 'Low resolution', gps: 'Estimated, partial', dashcams: 'Limited angles, moving shots', government: 'Intersection-based only' },
                  { feature: 'Use Cases', orchestra: 'Any vertical, real-time insights', satellite: 'Macro trends', gps: 'Basic movement only', dashcams: 'Limited to vehicle footage', government: 'Law enforcement only' }
                ].map((row, index) => (
                  <div key={index} className="bg-gray-800 rounded-lg p-3 mb-3 shadow-md">
                    <p className="text-base text-white text-center mb-3 pb-2 border-b border-gray-600" style={{ fontFamily: 'Montserrat, sans-serif' }}>
                      {row.feature}
                    </p>
                    <div className="flex justify-between items-center">
                      <div className="text-center w-1/2">
                        <p className="font-bold text-blue-400 text-sm mb-1">Orchestra</p>
                        <p className="text-white text-sm">{row.orchestra}</p>
                      </div>
                      <div className="text-center w-1/2">
                        <p className="font-bold text-gray-400 text-sm mb-1">Others</p>
                        <p className="text-gray-400 text-sm">{row.satellite}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Desktop table remains unchanged */}
              <table className="hidden md:table w-full bg-gray-900 shadow-lg rounded-lg overflow-hidden">
                <thead>
                  <tr>
                    <th className="p-3 text-left text-lg font-bold text-white bg-gray-800" style={{ width: '25%', fontFamily: 'Montserrat, sans-serif' }}>Feature</th>
                    <th className="p-3 text-left text-lg font-bold text-blue-400 bg-blue-900" style={{ width: '15%', fontFamily: 'Montserrat, sans-serif', borderBottom: '2px solid #007BFF' }}>Orchestra (Unified Camera Network)</th>
                    <th className="p-3 text-left text-lg font-bold text-gray-400 bg-gray-800" style={{ width: '15%', fontFamily: 'Montserrat, sans-serif' }}>Satellite</th>
                    <th className="p-3 text-left text-lg font-bold text-gray-400 bg-gray-800" style={{ width: '15%', fontFamily: 'Montserrat, sans-serif' }}>Phone GPS Data</th>
                    <th className="p-3 text-left text-lg font-bold text-gray-400 bg-gray-800" style={{ width: '15%', fontFamily: 'Montserrat, sans-serif' }}>Dashcams / Vehicle Cameras</th>
                    <th className="p-3 text-left text-lg font-bold text-gray-400 bg-gray-800" style={{ width: '15%', fontFamily: 'Montserrat, sans-serif' }}>Government Cameras (Flock, etc.)</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    { feature: 'Type', orchestra: '24/7 video footage', satellite: 'Still images', gps: 'Location pings', dashcams: 'Vehicle-based video', government: 'Mostly still images, some video' },
                    { feature: 'Coverage', orchestra: 'Unified, citywide', satellite: 'Large area, low res', gps: 'Fragmented', dashcams: 'Fragmented, moving vehicles', government: 'Spotty, intersection-focused' },
                    { feature: 'Update Speed', orchestra: 'Real-time, continuous', satellite: 'Slow refresh', gps: 'Laggy, incomplete', dashcams: 'Not citywide, not shared', government: 'Delayed, not always accessible' },
                    { feature: 'Data Quality', orchestra: 'Ground-truth, high-res video', satellite: 'Low resolution', gps: 'Estimated, partial', dashcams: 'Limited angles, moving shots', government: 'Intersection-based only' },
                    { feature: 'Use Cases', orchestra: 'Any vertical, real-time insights', satellite: 'Macro trends', gps: 'Basic movement only', dashcams: 'Limited to vehicle footage', government: 'Law enforcement only' }
                  ].map((row, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-gray-800' : 'bg-gray-900'}>
                      <td className="p-3 border-t font-bold text-white" style={{ padding: '10px', borderBottom: '1px solid #444', fontFamily: 'Montserrat, sans-serif' }}>
                        {row.feature}
                      </td>
                      <td className="p-3 border-t text-blue-400" style={{ padding: '10px', borderBottom: '1px solid #444', fontFamily: 'Montserrat, sans-serif' }}>{row.orchestra}</td>
                      <td className="p-3 border-t text-gray-400" style={{ padding: '10px', borderBottom: '1px solid #444', fontFamily: 'Montserrat, sans-serif' }}>{row.satellite}</td>
                      <td className="p-3 border-t text-gray-400" style={{ padding: '10px', borderBottom: '1px solid #444', fontFamily: 'Montserrat, sans-serif' }}>{row.gps}</td>
                      <td className="p-3 border-t text-gray-400" style={{ padding: '10px', borderBottom: '1px solid #444', fontFamily: 'Montserrat, sans-serif' }}>{row.dashcams}</td>
                      <td className="p-3 border-t text-gray-400" style={{ padding: '10px', borderBottom: '1px solid #444', fontFamily: 'Montserrat, sans-serif' }}>{row.government}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        <p className="text-center text-white/80 font-light md:max-w-3xl mx-auto">
          Want to see how this data can work for you?
        </p>

        {/* CTA Button */}
        <div className="text-center mt-2 px-4">
          <a
            href="https://calendly.com/drake-orchestraos"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 px-8 py-3 bg-gradient-to-r from-blue-600 to-blue-400 rounded-full text-white font-medium tracking-wide transform transition hover:scale-105 hover:shadow-lg focus:outline-none cursor-pointer max-w-xs mx-auto inline-block"
          >
            <p className="text-lg md:text-xl font-light leading-none">
              Request a demo
            </p>
          </a>
        </div>
        <img src="/images/beige-spur.svg" alt="" className="absolute top-8 right-8 w-16 h-16 hidden md:block" />
      </div>


      {/* Problem Section */}
      {/* <div ref={problemSectionRef} className="h-screen relative snap-start bg-black flex items-center">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h2 className="text-4xl md:text-5xl text-white font-light mb-16 text-center max-w-4xl mx-auto leading-tight">
              Every day, trillion-dollar decisions are made on bad data.
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12">

              <div>
                <h3 className="text-3xl text-white font-bold mb-8 text-center">Government Uses</h3>
                <div className="space-y-12">
                  <p className={`text-2xl md:text-3xl text-white/70 font-light transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} delay-[600ms]`}>
                    <span className="block mb-2 text-red-400 font-bold">LAW ENFORCEMENT</span>
                    <span className="text-white">Fails to convict—no clear video evidence</span>
                  </p>
                  <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition">Learn More</button>
                </div>
              </div>


              <div>
                <h3 className="text-3xl text-white font-bold mb-8 text-center">Commercial Uses</h3>
                <div className="space-y-12">
                  <p className={`text-2xl md:text-3xl text-white/70 font-light transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} delay-[150ms]`}>
                    <span className="block mb-2 text-blue-400 font-bold">INSURANCE</span>
                    <span className="text-white">Settle $1T+ in claims based on hearsay</span>
                  </p>
                  <p className={`text-2xl md:text-3xl text-white/70 font-light transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} delay-[300ms]`}>
                    <span className="block mb-2 text-green-400 font-bold">HEDGE FUNDS</span>
                    <span className="text-white">Trade billions on laggy, messy world data</span>
                  </p>
                  <p className={`text-2xl md:text-3xl text-white/70 font-light transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} delay-[450ms]`}>
                    <span className="block mb-2 text-purple-400 font-bold">MOBILITY</span>
                    <span className="text-white">Estimate traffic from fractions of phone GPS data</span>
                  </p>
                  <p className={`text-2xl md:text-3xl text-white/70 font-light transition-all duration-500 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'} delay-[750ms]`}>
                    <span className="block mb-2 text-amber-400 font-bold">COMMERCIAL REAL ESTATE</span>
                    <span className="text-white">Bet billions on flawed foot traffic estimates</span>
                  </p>
                  <button className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition">Learn More</button>
                </div>
              </div>
            </div>
          </div >
        </div >
        <img src="/images/beige-spur.svg" alt="" className="absolute top-8 right-8 w-16 h-16" />
      </div > */}

      {/* Camera Vision Section */}
      {/* < div
        id="camera-vision-section"
        className="h-screen relative snap-start bg-white flex items-center justify-center overflow-hidden"
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl md:text-6xl text-black font-light mb-6 text-center">
              What our cameras see
            </h2>
          </div>
        </div>
        <img src="/images/black-spur.svg" alt="" className="absolute top-8 right-8 w-16 h-16" />
      </div > */}

      {/* Portal Section */}
      {/* < div
        ref={portalSectionRef}
        id="portal-section"
        className="h-screen relative snap-start bg-white flex items-center justify-center overflow-hidden portal-section"
        style={{ perspective: shouldAnimatePortal ? "2000px" : "none" }
        }
      >

        < div className="absolute inset-0 flex items-center justify-center" >
          <div className="text-center">
            <h2 className="text-5xl md:text-7xl text-black font-bold mb-8 transition-opacity duration-500"
              style={{ opacity: shouldAnimatePortal && scrollProgress > 0.3 ? 0 : 1 }}>

            </h2>


            <div
              className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 transition-all duration-4000"
              style={{
                transform: `
                  translate(-50%, -50%)
                  scale(${shouldAnimatePortal ? 1 + (scrollProgress * 30) : 1})  // Start at normal size, then grow
                `,
                transformOrigin: 'center center',
                zIndex: 1
              }}
            >
              <img
                src="/images/vision.svg"
                alt=""
                className="w-[50vmin] h-[50vmin]"
                style={{
                  opacity: shouldAnimatePortal ? 1 : 0,  // Keep it visible throughout the animation
                  transform: `scale(${1 + scrollProgress * 30})`  // Emphasize the scaling effect
                }}
              />
            </div>
          </div>
        </div >
      </div > */}

      {/* Black Demo Section */}
      {/* < div
        id="black-demo-section"
        className="h-screen relative snap-start bg-black flex items-center justify-center"
        style={{ scrollSnapAlign: 'start' }}
      >
        <div className="w-full h-full">
          <iframe
            className="w-full h-full"
            src="https://www.youtube.com/embed/xCdguJXnY98?start=30&autoplay=1&mute=1&controls=1&modestbranding=1&playsinline=1&rel=0"
            title="Orchestra Demo"
            frameBorder="0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
      </div > */}


      <div id="case-studies" className="relative md:snap-start bg-black min-h-screen flex flex-col justify-center py-16">
        {/* Section Title */}
        <div className="text-center max-w-4xl mx-auto px-4 mb-12">
          <h2 className="text-2xl md:text-4xl text-white font-light mb-2">
            Real-world results
          </h2>
          <div className="w-24 h-[1px] bg-gradient-to-r from-transparent via-blue-400 to-transparent mx-auto my-3"></div>
        </div>

        {/* Case Studies Grid */}
        <div className="container mx-auto px-4">
          <div className="flex flex-col md:flex-row gap-8 md:gap-12 max-w-6xl mx-auto">
            {/* Archangel Case - Internal Link */}
            <Link
              to="/archangel"
              className="flex-1 bg-black/30 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden group cursor-pointer hover:border-white/20 transition-all"
            >
              <div className="aspect-video relative overflow-hidden">
                <video
                  id="archangel-case"
                  className="w-full h-full object-cover"
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src="/videos/archeangel.mp4" type="video/mp4" />
                </video>
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl text-white font-medium mb-3 group-hover:text-blue-400 transition-colors">
                  Solved major crime: car theft
                </h3>
                <p className="text-white/70 text-lg mb-6 font-light">
                  Video footage from our camera network is now key evidence in a stolen car case.
                </p>
                <span className="inline-flex items-center text-blue-400 group-hover:text-blue-300 transition-colors">
                  Learn more →
                </span>
              </div>
            </Link>

            {/* ParkingKarma Case - External Link */}
            <a
              href="https://parkingkarma.io"
              target="_blank"
              rel="noopener noreferrer"
              className="flex-1 bg-black/30 backdrop-blur-sm rounded-xl border border-white/10 overflow-hidden group cursor-pointer hover:border-white/20 transition-all"
            >
              <div className="aspect-video relative overflow-hidden">
                <img
                  id="parking-karma-case"
                  className=" object-cover"
                  src="/images/pk.png"
                  style={{ width: '100%', height: '100%', objectFit: 'cover', justifyContent: 'center', alignItems: 'center', margin: 'auto' }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
              </div>
              <div className="p-6">
                <h3 className="text-2xl text-white font-medium mb-3 group-hover:text-blue-400 transition-colors">
                  50 hours of driver time saved
                </h3>
                <p className="text-white/70 text-lg mb-6 font-light">
                  San Francisco drivers find parking instantly with our real-time street parking availability API.
                </p>
                <span className="inline-flex items-center text-blue-400 group-hover:text-blue-300 transition-colors">
                  Learn more →
                </span>
              </div>
            </a>
          </div>
        </div>
        <img src="/images/beige-spur.svg" alt="" className="absolute top-8 right-8 w-16 h-16 hidden md:block" />

      </div>

      {/* Footer */}
      <footer className="bg-black border-t border-white/10 py-8 md:py-12 snap-start">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col md:flex-row justify-between items-center mb-8">
            <nav className="mb-6 md:mb-0">
              <ul className="flex flex-wrap justify-center md:justify-start gap-6 md:gap-8">
                <li>
                  <span className="text-white/60 hover:text-white/90 transition-colors text-sm cursor-not-allowed">
                    Docs
                    <span className="ml-2 text-xs text-white/40">(Coming Soon)</span>
                  </span>
                </li>
                <li>
                  <a
                    href="https://calendly.com/drake-orchestraos"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-white/60 hover:text-white/90 transition-colors text-sm"
                  >
                    Book a Call
                  </a>
                </li>
                <li>
                  <Link
                    to="/about"
                    className="text-white/60 hover:text-white/90 transition-colors text-sm"
                  >
                    About
                  </Link>
                </li>
                <li>
                  <Link
                    to="/privacy"
                    className="text-white/60 hover:text-white/90 transition-colors text-sm"
                  >
                    Privacy
                  </Link>
                </li>
                <li>
                  <Link
                    to="/careers"
                    className="text-white/60 hover:text-white/90 transition-colors text-sm"
                  >
                    Careers
                  </Link>
                </li>
              </ul>
            </nav>
          </div>

          <div>
            <div className="flex flex-col md:flex-row justify-between items-center">
              <p className="text-white/40 text-sm mb-4 md:mb-0">
                Built in San Francisco
              </p>
              <a
                href="mailto:drake@orchestraos.com"
                className="text-white/60 hover:text-blue-400 transition-colors text-sm group relative"
                title="drake@orchestraos.com"
              >
                <svg
                  className="w-5 h-5"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </footer>

    </div>
  );
}

const fadeInUpKeyframes = `
@keyframes fade-in-up {
  0% {
    opacity: 0;
    transform: translateY(1rem);
  }
  100% {
    opacity: 1;
    transform: translateY(0);
  }
}
`;

const style = document.createElement('style');
style.textContent = fadeInUpKeyframes;
document.head.appendChild(style);
